import React, { useEffect, useState } from 'react';
import SignaturePad from 'react-signature-canvas';
import SignatureCanvas from 'react-signature-canvas';
import { Form, Input, Select, Typography, Row, Col, Checkbox, Tabs, TabsProps, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { InputDate, ProvinceSelector } from 'afrodita';
import moment from 'moment';
import { isMobileOnly } from 'react-device-detect';

const { Text } = Typography;
const { Option } = Select;

type Props = {
	setDocSigned: any;
	nameBank: any;
	dataMortgage: any;
	clickTab: any;
	form: any;
	countries: any;
	sabadellSigned?: boolean;
	setSabadellSigned: any;
	platform: any;
	env: any;
};

export default function RenderSingFile({
	setDocSigned,
	nameBank,
	dataMortgage,
	clickTab,
	form,
	countries,
	sabadellSigned,
	setSabadellSigned,
	platform,
	env
}: Props) {
	const [dataURL, setDataURL] = React.useState<string | null>(null);
	const padRef = React.useRef<SignatureCanvas>(null);
	const padRefs = React.useRef<SignaturePad[]>([]);
	const { t } = useTranslation();
	const [index, setIndex] = useState(undefined);
	const [initialValues, setInitialValues] = useState(undefined);
	const [participant, setParticipant] = useState(undefined);
	const [isCheckboxDisabled, setIsCheckboxDisabled] = useState(true);
	const [ownerSignatures, setOwnerSignatures] = useState([]);

	const options = [
		{ label: 'marital.Single', value: 'single' },
		{ label: 'marital.community', value: 'married_property' },
		{ label: 'marital.Divorced', value: 'divorced' },
		{ label: 'marital.Widower', value: 'widowed' },
		{ label: 'marital.domesticCouple', value: 'unmarried_partner' },
		{ label: 'marital.separation', value: 'married_separation' }
	];

	useEffect(() => {
		const index2 = clickTab.charAt(clickTab.length - 1);
		setIndex(index2 - 1);
	}, []);

	useEffect(() => {
		if (dataMortgage && !isNaN(index)) {
			if (clickTab.includes('owner')) {
				setParticipant(dataMortgage?.owners[index]);
			} else {
				setParticipant(dataMortgage?.guarantors[index]);
			}
		}
	}, [index]);

	useEffect(() => {
		checkFields();
	}, [initialValues]);

	useEffect(() => {
		form.resetFields();
		if (!isNaN(index) && participant) {
			// En el consentimiento unificado el nombre y apellidos deben llegar sin autocompletar
			// (vacíos y editables); el resto de formularios de banco sí precargan estos datos.
			const isMultibank = nameBank === 'consent_multibank';
			const updatedValues = {
				name: !isMultibank && participant?.name ? participant.name : undefined,
				firstSurname:
					!isMultibank && participant?.firstSurname ? participant.firstSurname : undefined,
				lastSurname: !isMultibank && participant?.lastSurname ? participant.lastSurname : undefined,
				email: participant?.email ? participant?.email : undefined,
				nif: participant?.nif ? participant?.nif : undefined,
				phone: participant?.phone ? participant?.phone : undefined,
				address: participant?.address ? participant?.address : undefined,
				population: participant?.population ? participant?.population : undefined,
				postalCode: participant?.postalCode ? participant?.postalCode : undefined,
				birthdate: participant?.birthdate
					? moment(participant?.birthdate, 'YYYY-MM-DD').format('DD-MM-YYYY')
					: undefined,
				taxResidency: participant?.taxResidency ? participant?.taxResidency : undefined,
				birthCountry: participant?.birthCountry ? participant?.birthCountry : undefined,
				cityzenship: participant?.cityzenship ? participant?.cityzenship : undefined,
				nationality: participant?.nationality ? participant?.nationality : undefined,
				maritalStatus: participant?.maritalStatus ? participant?.maritalStatus : undefined,
				province: participant?.province ? participant.province : undefined
			};

			form.setFieldsValue(updatedValues);
			setInitialValues(updatedValues);
		}
	}, [index, participant]);

	useEffect(() => {
		const validSigns = ownerSignatures?.filter((s) => s && s.sign) ?? [];
		const allSigned = validSigns.length === dataMortgage?.owners.length;

		if (allSigned) {
			setDocSigned(validSigns);
		} else {
			setDocSigned(undefined);
		}
	}, [ownerSignatures, dataMortgage?.owners.length]);

	const checkFields = () => {
		const { name, firstSurname } = form.getFieldsValue();
		if (name && firstSurname) {
			setIsCheckboxDisabled(false);
		} else {
			setIsCheckboxDisabled(true);
		}
	};

	const clear = () => {
		padRef.current?.clear();
	};

	const trim = () => {
		const url = padRef.current?.getTrimmedCanvas().toDataURL('image/png');
		if (url) {
			setDataURL(url);
			setDocSigned(url);
		}
	};

	const trimOwnerSignature = (index: number) => {
		const pad = padRefs.current[index];
		if (pad) {
			const trimmedDataUrl = {
				owner: `owner${index + 1}`,
				sign: pad.getTrimmedCanvas().toDataURL('image/png')
			};
			setOwnerSignatures((prev) => {
				const newSigns = [...prev];
				newSigns[index] = trimmedDataUrl;
				return newSigns;
			});
		}
	};

	const clearOwnerSignature = (index: number) => {
		padRefs.current[index]?.clear();
		setOwnerSignatures((prev) => {
			const newSigns = [...prev];
			newSigns[index] = undefined;
			return newSigns;
		});
	};

	const abancaTabs = () => (
		<Form form={form} layout="vertical" style={{ width: '100%' }}>
			<Tabs
				className="tabs-owners"
				items={dataMortgage?.owners?.map((owner: any, index: number) => ({
					key: index + 1,
					label: `Titular ${index + 1}`,
					children: (
						<>
							<Row gutter={[16, 16]} align={'middle'}>
								<Col lg={8} md={12} sm={24} xs={24} xl={6} xxl={6}>
									<Form.Item
										className="input-sign"
										name={`owner${index + 1}-name`}
										label={t('form.name')}
										initialValue={owner.name}
										rules={[{ required: true, message: t('form.required2') }]}>
										<Input
											disabled={!!owner?.name}
											style={{ width: '100%', height: 40 }}
											placeholder={t('form.name')}
										/>
									</Form.Item>
								</Col>

								<Col lg={8} md={12} sm={24} xs={24} xl={6} xxl={6}>
									<Form.Item
										className="input-sign"
										name={`owner${index + 1}-firstSurname`}
										initialValue={owner.firstSurname}
										label={t('form.firstSurname')}
										rules={[{ required: true, message: t('form.required2') }]}>
										<Input
											disabled={!!owner?.firstSurname}
											style={{ width: '100%', height: 40 }}
											placeholder={t('form.firstSurname')}
										/>
									</Form.Item>
								</Col>

								<Col lg={8} md={12} sm={24} xs={24} xl={6} xxl={6}>
									<Form.Item
										className="input-sign"
										name={`owner${index + 1}-lastSurname`}
										initialValue={owner.lastSurname}
										label={t('form.lastSurname')}
										rules={[{ required: true, message: t('form.required2') }]}>
										<Input
											disabled={!!owner?.lastSurname}
											style={{ width: '100%', height: 40 }}
											placeholder={t('form.lastSurname')}
										/>
									</Form.Item>
								</Col>

								<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
									<Form.Item
										className="input-sign"
										name={`owner${index + 1}-email`}
										initialValue={owner.email}
										label={t('appraisal.emailSecure')}
										rules={[{ required: true, message: t('form.required2') }]}>
										<Input
											disabled={!!owner?.email}
											style={{ width: '100%', height: 40 }}
											placeholder={t('appraisal.emailSecure')}
										/>
									</Form.Item>
								</Col>

								<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
									<Form.Item
										className="input-sign"
										name={`owner${index + 1}-phone`}
										initialValue={owner.phone}
										label={t('form.phone')}
										rules={[{ required: true, message: t('form.required2') }]}>
										<Input
											disabled={!!owner?.phone}
											style={{ width: '100%', height: 40 }}
											placeholder={t('form.phone')}
										/>
									</Form.Item>
								</Col>

								<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
									<Form.Item
										className="input-sign"
										name={`owner${index + 1}-nif`}
										initialValue={owner.nif}
										label={t('form.identification')}
										rules={[{ required: true, message: t('form.required2') }]}>
										<Input
											disabled={!!owner?.nif}
											style={{ width: '100%', height: 40 }}
											placeholder={t('0000000X')}
										/>
									</Form.Item>
								</Col>

								<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8} style={{ marginBottom: 24 }}>
									<Form.Item
										className="input-sign"
										name={`owner${index + 1}-address`}
										initialValue={owner.address}
										label={t('address.ev')}
										rules={[{ required: true, message: t('form.required2') }]}>
										<Input
											disabled={!!owner?.address}
											style={{ width: '100%', height: 40 }}
											placeholder={t('calle...')}
										/>
									</Form.Item>
								</Col>

								<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
									<Form.Item
										className="input-sign"
										name={`owner${index + 1}-population`}
										initialValue={owner.population}
										label={t('form.population')}
										rules={[{ required: true, message: t('form.required2') }]}>
										<Input
											disabled={!!owner?.population}
											style={{ width: '100%', height: 40 }}
											placeholder={t('form.population')}
										/>
									</Form.Item>
								</Col>

								<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
									<Form.Item
										className="input-sign"
										name={`owner${index + 1}-postalCode`}
										initialValue={owner.postalCode}
										label={t('m_register.postalCode')}
										rules={[{ required: true, message: t('form.required2') }]}>
										<Input
											disabled={!!owner?.postalCode}
											style={{ width: '100%', height: 40 }}
											placeholder={t('m_register.postalCode')}
										/>
									</Form.Item>
								</Col>
							</Row>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									width: '100%'
								}}>
								<span style={{ marginTop: 24 }}>Firma del titular {index + 1}:</span>
								<SignaturePad
									ref={(ref) => {
										if (ref) padRefs.current[index] = ref;
									}}
									canvasProps={{ width: 300, height: 100, className: 'sigCanvas' }}
									onEnd={() => trimOwnerSignature(index)}
								/>
								<div className="sigPreview">
									<button
										className="button-primari-delete-gibobs"
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											marginTop: 8,
											height: 24,
											width: 120,
											color: '#ffffff',
											border: 'none'
										}}
										onClick={() => clearOwnerSignature(index)}>
										Borrar
									</button>
								</div>
							</div>
						</>
					)
				}))}
			/>
		</Form>
	);

	const kutxabankTabs = () => (
		<Form form={form} layout="vertical" style={{ width: '100%' }}>
			<Tabs
				className="tabs-owners"
				items={dataMortgage?.owners?.map((owner: any, index: number) => ({
					key: index + 1,
					label: `Titular ${index + 1}`,
					children: (
						<>
							<Row gutter={[16, 16]} align={'middle'} justify={'center'}>
								<Col lg={8} md={12} sm={24} xs={24} xl={6} xxl={6}>
									<Form.Item
										className="input-sign"
										name={`owner${index + 1}-nif`}
										initialValue={owner.nif}
										label={t('form.identification')}
										rules={[{ required: true, message: t('form.required2') }]}>
										<Input
											disabled={!!owner?.nif}
											style={{ width: '100%', height: 40 }}
											placeholder={t('0000000X')}
										/>
									</Form.Item>
								</Col>
							</Row>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									width: '100%'
								}}>
								<span style={{ marginTop: 24 }}>Firma del titular {index + 1}:</span>
								<SignaturePad
									ref={(ref) => {
										if (ref) padRefs.current[index] = ref;
									}}
									canvasProps={{ width: 300, height: 100, className: 'sigCanvas' }}
									onEnd={() => trimOwnerSignature(index)}
								/>
								<div className="sigPreview">
									<button
										className="button-primari-delete-gibobs"
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											marginTop: 8,
											height: 24,
											width: 120,
											color: '#ffffff',
											border: 'none'
										}}
										onClick={() => clearOwnerSignature(index)}>
										Borrar
									</button>
								</div>
							</div>
						</>
					)
				}))}
			/>
		</Form>
	);

	const multibankForm = () => (
		<Form
			form={form}
			initialValues={initialValues}
			onValuesChange={checkFields}
			layout="vertical"
			style={{ width: '100%' }}>
			{/* Fila 1: Datos Personales Principales */}
			<Row gutter={12} style={{ width: '100%' }}>
				<Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={6}>
					<Form.Item
						name="name"
						label={t('form.name')}
						rules={[{ required: true, message: t('form.required2') }]}>
						<Input
							//disabled={!!participant?.name}
							style={{ height: 40, width: '100%' }}
							placeholder={t('form.name')}
						/>
					</Form.Item>
				</Col>
				<Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={6}>
					<Form.Item
						name="firstSurname"
						label={t('form.firstSurname')}
						rules={[{ required: true, message: t('form.required2') }]}>
						<Input
							//disabled={!!participant?.firstSurname}
							style={{ height: 40, width: '100%' }}
							placeholder={t('form.firstSurname')}
						/>
					</Form.Item>
				</Col>
				<Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={6}>
					<Form.Item name="lastSurname" label={t('form.lastSurname')}>
						<Input
							//disabled={!!participant?.lastSurname}
							style={{ height: 40, width: '100%' }}
							placeholder={t('form.lastSurname')}
						/>
					</Form.Item>
				</Col>
				<Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={6}>
					<Form.Item
						name="nif"
						label={t('form.dni')}
						rules={[{ required: true, message: t('form.required2') }]}>
						<Input
							//disabled={!!participant?.nif}
							style={{ height: 40, width: '100%' }}
							placeholder="00000000X"
						/>
					</Form.Item>
				</Col>
				<Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={6} style={{ marginTop: -6 }}>
					<Form.Item
						name="birthdate"
						label={t('form.birthdate')}
						rules={[{ required: true, message: t('form.required2') }]}>
						<InputDate
							type="date"
							disabled={!!participant?.birthdate}
							style={{ height: 40, width: '100%' }}
						/>
					</Form.Item>
				</Col>
				<Col xs={12} sm={6} md={4} lg={8} xl={8} xxl={6} style={{ marginTop: -6 }}>
					<Form.Item
						className="input-sign"
						name="birthCountry"
						label={t('form.birthCountry')}
						rules={[{ required: true, message: t('form.required2') }]}
						style={{ flex: '1 1 200px' }}>
						<Select
							showSearch
							disabled={!!participant?.birthCountry}
							style={{ width: '100%' }}
							placeholder={t('form.birthCountry')}
							filterOption={(input: any, option: any) =>
								option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
								option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}>
							{countries &&
								countries.countries.map((item: any) => (
									<Option key={item.code} value={item.code}>
										{item.name_es}
									</Option>
								))}
						</Select>
					</Form.Item>
				</Col>

				{isMobileOnly && (
					<Col xs={12} sm={6} md={4} lg={8} xl={8} xxl={6} style={{ marginTop: -6 }}>
						<Form.Item
							name="maritalStatus"
							label={t('form.maritalStatus')}
							rules={[{ required: true, message: t('form.required2') }]}>
							<Select disabled={!!participant?.maritalStatus} style={{ height: 40, width: '100%' }}>
								{options.map((status) => (
									<Select.Option key={status.value} value={status.value}>
										{t(status.label)}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Col>
				)}
				{!isMobileOnly && (
					<Col xs={12} sm={6} md={4} lg={8} xl={8} xxl={6} style={{ marginTop: -6 }}>
						<Form.Item
							name="maritalStatus"
							label={t('form.maritalStatus')}
							rules={[{ required: true, message: t('form.required2') }]}>
							<Select disabled={!!participant?.maritalStatus} style={{ height: 40, width: '100%' }}>
								{options.map((status) => (
									<Select.Option key={status.value} value={status.value}>
										{t(status.label)}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Col>
				)}
			</Row>

			{/* Fila 2: Ubicación y Estado Civil */}
			<Row gutter={[16, 0]}>
				<Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={6} style={{ marginTop: -6 }}>
					<Form.Item
						name="address"
						label={t('address.ev')}
						rules={[{ required: true, message: t('form.required2') }]}>
						<Input
							disabled={!!participant?.address}
							style={{ height: 40, width: '100%' }}
							placeholder={t('calle...')}
						/>
					</Form.Item>
				</Col>
				<Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={6} style={{ marginTop: -6 }}>
					<Form.Item
						name="population"
						label={t('form.population')}
						rules={[{ required: true, message: t('form.required2') }]}>
						<Input
							disabled={!!participant?.population}
							style={{ height: 40, width: '100%' }}
							placeholder={t('form.population')}
						/>
					</Form.Item>
				</Col>
				<Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={6} style={{ marginTop: -6 }}>
					<Form.Item
						name="province"
						label={t('form.provinces')}
						rules={[{ required: true, message: t('form.required2') }]}>
						<ProvinceSelector
							disabled={!!participant?.province}
							style={{ height: 40, width: '100%' }}
						/>
					</Form.Item>
				</Col>
				<Col xs={12} sm={6} md={4} lg={8} xl={8} xxl={6} style={{ marginTop: -6 }}>
					<Form.Item
						name="postalCode"
						label={t('m_register.postalCode')}
						rules={[{ required: true, message: t('form.required2') }]}>
						<Input
							disabled={!!participant?.postalCode}
							style={{ height: 40, width: '100%' }}
							placeholder="CP"
						/>
					</Form.Item>
				</Col>
			</Row>

			{/* Fila 3: Nacionalidad y Contacto */}
			<Row gutter={[16, 0]}>
				<Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={6} style={{ marginTop: -6 }}>
					<Form.Item
						name="nationality"
						label={t('form.nationality')}
						rules={[{ required: true, message: t('form.required2') }]}>
						<Select
							showSearch
							disabled={!!participant?.nationality}
							style={{ height: 40, width: '100%' }}>
							{countries?.countries.map((item: any) => (
								<Select.Option key={item.code} value={item.code}>
									{item.name_es}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Col>
				<Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={6} style={{ marginTop: -6 }}>
					<Form.Item
						name="cityzenship"
						label={t('form.country')}
						rules={[{ required: true, message: t('form.required2') }]}>
						<Select
							showSearch
							disabled={!!participant?.cityzenship}
							style={{ height: 40, width: '100%' }}>
							{countries?.countries.map((item: any) => (
								<Select.Option key={item.code} value={item.code}>
									{item.name_es}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Col>
				<Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={6} style={{ marginTop: -6 }}>
					<Form.Item
						name="taxResidency"
						label={t('form.taxResidency')}
						rules={[{ required: true, message: t('form.required2') }]}>
						<Select
							showSearch
							disabled={!!participant?.taxResidency}
							style={{ height: 40, width: '100%' }}>
							{countries?.countries.map((item: any) => (
								<Select.Option key={item.code} value={item.code}>
									{item.name_es}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Col>
				<Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={6} style={{ marginTop: -6 }}>
					<Form.Item
						name="phone"
						label={t('form.phone')}
						rules={[{ required: true, message: t('form.required2') }]}>
						<Input
							disabled={!!participant?.phone}
							style={{ height: 40, width: '100%' }}
							placeholder="600 000 000"
						/>
					</Form.Item>
				</Col>
				<Col
					xs={24}
					sm={12}
					md={8}
					lg={8}
					xl={8}
					xxl={6}
					style={{ marginTop: -6, marginBottom: -24 }}>
					<Form.Item
						name="email"
						label={t('appraisal.emailSecure')}
						rules={[
							{ type: 'email', message: t('form.emailNotValid') },
							{ required: true, message: t('form.required2') }
						]}>
						<Input
							disabled={!!participant?.email}
							style={{ height: 40, width: '100%' }}
							placeholder="email@ejemplo.com"
						/>
					</Form.Item>
				</Col>
			</Row>
		</Form>
	);

	return (
		<div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
			{nameBank === 'consent_abanca' && abancaTabs()}
			{nameBank === 'consent_multibank' && multibankForm()}
			{nameBank === 'consent_kutxabank' && kutxabankTabs()}

			{initialValues &&
				nameBank !== 'consent_caixabank' &&
				nameBank !== 'consent_bankinter' &&
				nameBank !== 'consent_abanca' &&
				nameBank !== 'consent_kutxabank' &&
				nameBank !== 'consent_multibank' && (
					<Form
						form={form}
						initialValues={initialValues}
						onValuesChange={checkFields}
						layout="vertical"
						style={{ width: '100%' }}>
						<Row gutter={[16, 16]} align={'middle'}>
							<Col lg={8} md={12} sm={24} xs={24} xl={6} xxl={6}>
								<Form.Item
									className="input-sign"
									name="name"
									label={<span>{t('form.name')}</span>}
									rules={[{ required: true, message: t('form.required2') }]}
									style={{ flex: '1 1 200px' }}>
									<Input
										disabled={!!participant?.firstSurname}
										style={{ width: '100%', height: 40 }}
										placeholder={t('form.name')}
									/>
								</Form.Item>
							</Col>
							<Col lg={8} md={12} sm={24} xs={24} xl={6} xxl={6}>
								<Form.Item
									className="input-sign"
									name="firstSurname"
									label={<span>{t('form.firstSurname')}</span>}
									rules={[{ required: true, message: t('form.required2') }]}
									style={{ flex: '1 1 200px' }}>
									<Input
										disabled={!!participant?.firstSurname}
										style={{ width: '100%', height: 40 }}
										placeholder={t('form.firstSurname')}
									/>
								</Form.Item>
							</Col>
							<Col lg={8} md={12} sm={24} xs={24} xl={6} xxl={6}>
								<Form.Item
									className="input-sign"
									name="lastSurname"
									label={<span>{t('form.lastSurname')}</span>}
									rules={[{ required: true, message: t('form.required2') }]}
									style={{ flex: '1 1 200px' }}>
									<Input
										disabled={!!participant?.lastSurname}
										style={{ width: '100%', height: 40 }}
										placeholder={t('form.lastSurname')}
									/>
								</Form.Item>
							</Col>
							{nameBank === 'consent_sabadell' && (
								<Col lg={8} md={12} sm={24} xs={24} xl={6} xxl={6}>
									<Form.Item
										className="input-sign"
										name="nif"
										label={<span>{t('form.dni')}</span>}
										rules={[{ required: true, message: t('form.required2') }]}
										style={{ flex: '1 1 200px' }}>
										<Input
											disabled={!!participant?.nif}
											style={{ width: '100%', height: 40 }}
											placeholder={t('0000000X')}
										/>
									</Form.Item>
								</Col>
							)}
							{nameBank !== 'consent_caja_rural_sur' &&
								nameBank !== 'consent_sabadell' &&
								nameBank !== 'consent_globalcaja' && (
									<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
										<Form.Item
											className="input-sign"
											name="email"
											label={t('appraisal.emailSecure')}
											rules={[
												{ type: 'email', message: t('form.emailNotValid') },
												{ required: true, message: t('form.required2') }
											]}
											style={{ flex: '1 1 200px' }}>
											<Input
												disabled={!!participant?.email}
												style={{ width: '100%', height: 40 }}
												placeholder={t('appraisal.emailSecure')}
											/>
										</Form.Item>
									</Col>
								)}
							{nameBank !== 'consent_sabadell' && (
								<>
									{nameBank !== 'consent_globalcaja' && (
										<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
											<Form.Item
												className="input-sign"
												name="phone"
												label={t('form.phone')}
												rules={[{ required: true, message: t('form.required2') }]}
												style={{ flex: '1 1 200px' }}>
												<Input
													disabled={!!participant?.phone}
													style={{ width: '100%', height: 40 }}
													placeholder={t('form.phone')}
												/>
											</Form.Item>
										</Col>
									)}
									{nameBank === 'consent_globalcaja' && (
										<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
											<Form.Item
												className="input-sign"
												name="nif"
												label={t('form.identification')}
												rules={[{ required: true, message: t('form.required2') }]}
												style={{ flex: '1 1 200px' }}>
												<Input
													disabled={!!participant?.nif}
													style={{ width: '100%', height: 40 }}
													placeholder={t('0000000X')}
												/>
											</Form.Item>
										</Col>
									)}

									{nameBank !== 'consent_globalcaja' && (
										<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8} style={{ marginBottom: 24 }}>
											<Form.Item
												className="input-sign"
												name="address"
												label={t('address.ev')}
												rules={[{ required: true, message: t('form.required2') }]}
												style={{ flex: '1 1 200px' }}>
												<Input
													disabled={!!participant?.address}
													style={{ width: '100%', height: 40 }}
													placeholder={t('calle...')}
												/>
											</Form.Item>
										</Col>
									)}
								</>
							)}
							{nameBank === 'consent_ibercaja' && (
								<>
									<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
										<Form.Item
											name="maritalStatus"
											label={t('form.maritalStatus')}
											rules={[{ required: true, message: t('form.required2') }]}
											style={{ width: '100%', height: 40 }}>
											<Select
												placeholder={t('form.maritalStatus')}
												style={{ width: '100%' }}
												disabled={!!participant?.maritalStatus}>
												{options.map((status) => {
													return (
														<Option key={status.value} value={status.value}>
															{t(status.label)}
														</Option>
													);
												})}
											</Select>
										</Form.Item>
									</Col>
									<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
										<Form.Item
											className="input-sign"
											name="nationality"
											label={t('form.nationality')}
											rules={[{ required: true, message: t('form.required2') }]}
											style={{ flex: '1 1 200px' }}>
											<Select
												showSearch
												disabled={!!participant?.nationality}
												style={{ width: '100%' }}
												placeholder={t('form.nationality')}
												filterOption={(input: any, option: any) =>
													option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
													option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
												}>
												{countries &&
													countries.countries.map((item: any) => (
														<Option key={item.code} value={item.code}>
															{item.name_es}
														</Option>
													))}
											</Select>
										</Form.Item>
									</Col>

									<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
										<Form.Item
											className="input-sign"
											name="cityzenship"
											label={t('form.country')}
											rules={[{ required: true, message: t('form.required2') }]}
											style={{ flex: '1 1 200px' }}>
											<Select
												showSearch
												disabled={!!participant?.cityzenship}
												style={{ width: '100%' }}
												placeholder={t('form.country')}
												filterOption={(input: any, option: any) =>
													option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
													option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
												}>
												{countries &&
													countries.countries.map((item: any) => (
														<Option key={item.code} value={item.code}>
															{item.name_es}
														</Option>
													))}
											</Select>
										</Form.Item>
									</Col>

									<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
										<Form.Item
											className="input-sign"
											name="birthCountry"
											label={t('form.birthCountry')}
											rules={[{ required: true, message: t('form.required2') }]}
											style={{ flex: '1 1 200px' }}>
											<Select
												showSearch
												disabled={!!participant?.birthCountry}
												style={{ width: '100%' }}
												placeholder={t('form.birthCountry')}
												filterOption={(input: any, option: any) =>
													option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
													option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
												}>
												{countries &&
													countries.countries.map((item: any) => (
														<Option key={item.code} value={item.code}>
															{item.name_es}
														</Option>
													))}
											</Select>
										</Form.Item>
									</Col>

									<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
										<Form.Item
											className="input-sign"
											name="taxResidency"
											label={t('form.taxResidency')}
											rules={[{ required: true, message: t('form.required2') }]}
											style={{ flex: '1 1 200px' }}>
											<Select
												showSearch
												disabled={!!participant?.taxResidency}
												style={{ width: '100%' }}
												placeholder={t('form.taxResidency')}
												filterOption={(input: any, option: any) =>
													option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
													option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
												}>
												{countries &&
													countries.countries.map((item: any) => (
														<Option key={item.code} value={item.code}>
															{item.name_es}
														</Option>
													))}
											</Select>
										</Form.Item>
									</Col>
									<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
										<Form.Item
											className="input-sign"
											name="birthdate"
											label={t('form.birthdate')}
											rules={[{ required: true, message: t('form.required2') }]}
											style={{ flex: '1 1 200px' }}>
											<InputDate
												type="date"
												style={{ width: '100%', height: 40 }}
												disabled={!!participant?.birthdate}
											/>
										</Form.Item>
									</Col>
								</>
							)}
							{nameBank === 'consent_sabadell_extra' && (
								<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
									<Form.Item
										className="input-sign"
										name="birthdate"
										label={t('form.birthdate')}
										rules={[{ required: true, message: t('form.required2') }]}
										style={{ flex: '1 1 200px' }}>
										<InputDate
											type="date"
											style={{ width: '100%', height: 40 }}
											disabled={!!participant?.birthdate}
										/>
									</Form.Item>
								</Col>
							)}

							{(nameBank === 'consent_caja_rural_sur' || nameBank === 'consent_ibercaja') && (
								<>
									<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
										<Form.Item
											className="input-sign"
											name="address"
											label={t('form.address')}
											rules={[{ required: true, message: t('form.required2') }]}
											style={{ flex: '1 1 200px' }}>
											<Input
												disabled={!!participant?.address}
												style={{ width: '100%', height: 40 }}
												placeholder={t('form.address')}
											/>
										</Form.Item>
									</Col>
									<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
										<Form.Item
											className="input-sign"
											name="population"
											label={t('form.population')}
											rules={[{ required: true, message: t('form.required2') }]}
											style={{ flex: '1 1 200px' }}>
											<Input
												disabled={!!participant?.population}
												style={{ width: '100%', height: 40 }}
												placeholder={t('form.population')}
											/>
										</Form.Item>
									</Col>
									{nameBank === 'consent_ibercaja' && (
										<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
											<Form.Item
												className="select-sign"
												name="province"
												label={t('form.provinces')}
												rules={[{ required: true, message: t('form.required2') }]}
												style={{ flex: '1 1 200px' }}>
												<ProvinceSelector
													disabled={!!participant?.province}
													placeholder={t('form.provinces')}
													style={{ width: '100%', height: 40 }}
												/>
											</Form.Item>
										</Col>
									)}
									<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
										<Form.Item
											className="input-sign"
											name="postalCode"
											label={t('m_register.postalCode')}
											rules={[{ required: true, message: t('form.required2') }]}
											style={{ flex: '1 1 200px' }}>
											<Input
												disabled={!!participant?.postalCode}
												style={{ width: '100%', height: 40 }}
												placeholder={t('m_register.postalCode')}
											/>
										</Form.Item>
									</Col>
								</>
							)}
						</Row>
					</Form>
				)}
			{initialValues && nameBank === 'consent_bankinter' && (
				<Form form={form} initialValues={initialValues} layout="vertical" style={{ width: '100%' }}>
					<Row gutter={[16, 16]} align={'middle'} style={{ marginTop: 24 }}>
						<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
							<Form.Item
								className="input-sign"
								name="name"
								label={t('form.name')}
								rules={[{ required: true, message: t('form.required2') }]}
								style={{ flex: '1 1 200px' }}>
								<Input
									disabled={!!participant?.name}
									style={{ width: '100%', height: 40 }}
									placeholder={t('form.name')}
								/>
							</Form.Item>
						</Col>
						<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
							<Form.Item
								className="input-sign"
								name="email"
								label={t('appraisal.emailSecure')}
								rules={[
									{ type: 'email', message: t('form.emailNotValid') },
									{ required: true, message: t('form.required2') }
								]}
								style={{ flex: '1 1 200px' }}>
								<Input
									disabled={!!participant?.email}
									style={{ width: '100%', height: 40 }}
									placeholder={t('appraisal.emailSecure')}
								/>
							</Form.Item>
						</Col>
						<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8}>
							<Form.Item
								className="input-sign"
								name="phone"
								label={t('form.phone')}
								rules={[{ required: true, message: t('form.required2') }]}
								style={{ flex: '1 1 200px' }}>
								<Input
									disabled={!!participant?.phone}
									style={{ width: '100%', height: 40 }}
									placeholder={t('form.phone')}
								/>
							</Form.Item>
						</Col>
						<Col lg={8} md={12} sm={24} xs={24} xl={8} xxl={8} style={{ marginBottom: 24 }}>
							<Form.Item
								className="input-sign"
								name="nif"
								label={t('form.identification')}
								rules={[{ required: true, message: t('form.required2') }]}
								style={{ flex: '1 1 200px' }}>
								<Input
									disabled={!!participant?.nif}
									style={{ width: '100%', height: 40 }}
									placeholder={t('0000000X')}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			)}
			{nameBank !== 'consent_sabadell' &&
				nameBank !== 'consent_abanca' &&
				nameBank !== 'consent_kutxabank' && (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							width: '100%'
						}}>
						<span style={{ marginTop: 24 }}>{t('sign.here')}:</span>
						<SignaturePad
							ref={padRef}
							canvasProps={{ width: 300, height: 100, className: 'sigCanvas' }}
							onEnd={trim}
						/>
						<div className="sigPreview">
							<button
								className="button-primari-delete-gibobs"
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									marginTop: 8,
									height: 24,
									width: 120,
									color: '#ffffff',
									border: 'none'
								}}
								onClick={clear}>
								Borrar
							</button>
						</div>
					</div>
				)}
			{nameBank === 'consent_sabadell' && (
				<div style={{ display: 'flex', justifyContent: 'center', marginTop: 16, marginBottom: 24 }}>
					<Checkbox
						disabled={isCheckboxDisabled}
						value={sabadellSigned}
						onChange={(e) => setSabadellSigned(e.target.checked)}>
						<Text style={{ fontSize: 12, fontWeight: 400, color: '#424242' }}>
							{t('doc.sabadellSign')}
						</Text>
					</Checkbox>
				</div>
			)}
		</div>
	);
}
